import React from 'react'
import ReactDOM from 'react-dom'

const ixObserverSupported = typeof IntersectionObserver === 'function'

const isScrollable = el =>
  /scroll|auto/.test(getComputedStyle(el).overflow) //TODO maybe `hidden` too? Greater chance of false positive


/**
 * ScrollBoundsAware:
 *
 * HOC that watches a component's position in relation to its nearest scrollable container
 * and gives is a `isInScrollBounds` prop depending on whether it is currently scrolled in/out
 * of view.
 *
 * This prop can then be used by the wrapped component to do things like:
 *
 *   - Skip updates if not in view by checking it in a `shouldComponentUpdate` lifecycle method
 *   - Stop/pause animation loops while out of view
 *   - Remove particularly heavy portions of its DOM while not in view
 *     (NOTE: if you do this, be careful that the changes themselves do not cause the component
 *     to go back in or out of view; if so then you risk getting into an update loop.)
 *
 * The `isInScrollBounds` prop will be `undefined` by default prior to first being mounted in the
 * DOM; this allows the wrapped component to use its `defaultProps` to control whether it should
 * initially render in visible or hidden state.
 *
 * Intersections are detected via `IntersectionObserver` in browsers that support it, for best
 * performance, falling back to debounced scroll event listeners + bounding rect queries otherwise.
 *
 * TODO:
 *   - Add a `scrollBoundsBuffer` prop or HOC config, mapping to IntersectionObserver's `rootMargin` option
 *   - Make use of InteractionObserver's ratio thresholds?
 *   - Debounce/buffer the observer callback?
 *
 * @param {class} WrappedComponent
 * @return {class}
 */
export default function(WrappedComponent) {
  class ScrollBoundsAware extends React.Component {
    state = {
      isInScrollBounds: undefined
    }

    componentDidMount() {
      // Find the nearest scroll parent - if none then the browser viewport will be used
      const thisEl = ReactDOM.findDOMNode(this)
      let scrollEl = thisEl
      do {
        scrollEl = scrollEl.parentNode
      } while (scrollEl && !isScrollable(scrollEl))

      if (ixObserverSupported) {
        // Set up IntersectionObserver
        const observer = new IntersectionObserver(this._ixChange, {root: scrollEl || null})
        this._cleanupIxObserver = () => {
          observer.unobserve(thisEl)
        }
        observer.observe(thisEl)
      } else {
        // No IntersectionObserver support; add scroll event listener to mimic it
        scrollEl = scrollEl || document.documentElement
        let timer = null
        const checkBounds = () => {
          if (!this._unmounted) {
            const clipRect = scrollEl.getBoundingClientRect()
            const ourRect = thisEl.getBoundingClientRect()
            const isIntersecting = ourRect.top < clipRect.bottom
              && ourRect.bottom > clipRect.top
              && ourRect.left < clipRect.right
              && ourRect.right > clipRect.left
            this._ixChange([{isIntersecting}])
          }
          timer = null
        }
        const onScroll = () => {
          // Buffer bounds checks to preserve smoothest scrolling
          if (timer) cancelAnimationFrame(timer)
          timer = requestAnimationFrame(checkBounds)
        }
        scrollEl.addEventListener('scroll', onScroll)
        this._cleanupIxObserver = () => {
          scrollEl.removeEventListener('scroll', onScroll)
        }
        onScroll()
      }
    }

    componentWillUnmount() {
      this._unmounted = true
      const cleanup = this._cleanupIxObserver
      if (cleanup) cleanup()
    }

    _ixChange = ixEntries => {
      if (ixEntries && ixEntries[0]) { //should only ever be 1 entry
        const isInScrollBounds = ixEntries[0].isIntersecting || false
        if (isInScrollBounds !== this.state.isInScrollBounds) {
          this.setState({isInScrollBounds})
        }
      }
    }

    render() {
      return <WrappedComponent 
        isInScrollBounds={this.state.isInScrollBounds} 
        {...this.props} 
      />
    }
  }

  return ScrollBoundsAware
}